import { CircleCheck } from 'lucide-react'
import { useMatch } from 'react-router'
import { H4 } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { gql } from '@/lib/graphql/gql'
import { useCompanyPath, useIdCompany, useSuspenseQuery } from '@/lib/hooks'
import { ToggleMandatorySsoDialog } from '@/routes/single-sign-on/mandatory-sso/toggle-mandatory-sso-dialog'
import type {
  MandatorySsoQuery,
  MandatorySsoQueryVariables,
} from '@/routes/single-sign-on/mandatory-sso/index.types.gen'

const MANDATORY_SSO_QUERY = gql`
  query MandatorySso($idCompany: String!) {
    company(id: $idCompany) {
      id
      samlSetting @live {
        id
        isForced
      }
      draftSamlSetting: samlSetting(isDraft: true) @live {
        id
        isForced
      }
    }
  }
`

export const MandatorySso = () => {
  const { idCompany } = useIdCompany()
  const { navigateToPath } = useCompanyPath()
  const isDialogOpen = Boolean(useMatch('/single-sign-on/toggle-force-sso'))

  const { data } = useSuspenseQuery<
    MandatorySsoQuery,
    MandatorySsoQueryVariables
  >(MANDATORY_SSO_QUERY, {
    variables: { idCompany },
  })

  const liveSamlSetting = data?.company?.samlSetting
  const draftSamlSetting = data?.company?.draftSamlSetting
  const isSsoMandatory =
    liveSamlSetting?.isForced ?? draftSamlSetting?.isForced ?? false

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            <H4>Mandatory single sign-on</H4>
          </CardTitle>
          <CardAction>
            <Button
              variant="outline"
              onClick={() =>
                navigateToPath({
                  pathname: '/single-sign-on/toggle-force-sso',
                  options: { replace: true },
                })
              }
            >
              {isSsoMandatory ? 'Disable' : 'Enable'}
            </Button>
          </CardAction>
          <CardDescription>
            When enabled, users can only log in with SSO. When not enabled,
            users can also sign in with a password.
          </CardDescription>
        </CardHeader>
        {isSsoMandatory && (
          <CardContent>
            <div className="flex items-center gap-2">
              <CircleCheck className="size-4 fill-emerald-600 stroke-background" />
              <span className="text-sm text-green-600 dark:text-green-400">
                Enabled
              </span>
            </div>
          </CardContent>
        )}
      </Card>
      <ToggleMandatorySsoDialog
        isOpen={isDialogOpen}
        onClose={() =>
          navigateToPath({
            pathname: '/single-sign-on',
            options: { replace: true },
          })
        }
      />
    </>
  )
}
